import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Phone, Globe, Menu, X, ChevronDown } from "lucide-react";
import { BRAND } from "../mock";
import { useLang } from "../i18n/LanguageContext";
import { LANGS } from "../i18n/translations";

const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = !isHome || scrolled;

  const LINKS = [
    { label: t("nav.book"), to: "/#book" },
    { label: t("nav.prices"), to: "/preise" },
    { label: t("nav.fleet"), to: "/#fleet" },
    { label: t("nav.destinations"), to: "/ziele" },
    { label: t("nav.blog"), to: "/ratgeber" },
    { label: t("nav.business"), to: "/firmenkunden" },
    { label: t("nav.travel"), to: "/reisebuero" },
    { label: t("nav.contact"), to: "/#contact" },
  ];

  const go = (e, to) => {
    e.preventDefault();
    setOpen(false);
    if (to.startsWith("/#")) {
      const hash = to.slice(1);
      if (isHome) {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/" + hash);
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${solid ? "bg-white/90 backdrop-blur-md border-b border-[#E9E5DE] elev-soft" : "bg-transparent"}`}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[68px] flex items-center justify-between">
        <Link to="/" className="flex items-center shrink-0 bg-white rounded-xl px-3 py-1 elev-soft">
          <img src={BRAND.logo} alt={BRAND.name} className="h-9 w-auto object-contain" />
        </Link>

        <div className="hidden lg:flex items-center gap-5">
          {LINKS.map((link, i) => (
            <a key={i} href={link.to} onClick={(e) => go(e, link.to)} className={`font-body text-sm transition-colors duration-200 ${solid ? "text-[#5A607A] hover:text-[#0B1533]" : "text-white/85 hover:text-white"}`}>{link.label}</a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {/* Language switcher */}
          <div className="relative">
            <button onClick={() => setLangOpen(!langOpen)} onBlur={() => setTimeout(() => setLangOpen(false), 150)} className={`flex items-center gap-1 font-body text-sm px-2 py-1.5 rounded-lg transition-colors ${solid ? "text-[#5A607A] hover:text-[#0B1533]" : "text-white/85 hover:text-white"}`}>
              <Globe size={16} /> {lang.toUpperCase()} <ChevronDown size={13} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-36 bg-white rounded-xl border border-[#E9E5DE] elev-lg py-1 overflow-hidden">
                {LANGS.map((l) => (
                  <button key={l.code} data-testid={`lang-${l.code}`} onMouseDown={() => { setLang(l.code); setLangOpen(false); navigate("/" + l.code); }} className={`w-full text-left px-4 py-2 font-body text-sm hover:bg-[#F5F2ED] transition-colors ${lang === l.code ? "text-[#2B4BFF] font-semibold" : "text-[#0B1533]"}`}>{l.name}</button>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => setOpen(!open)} className={`lg:hidden ${solid ? "text-[#0B1533]" : "text-white"}`} aria-label="Menu">{open ? <X size={26} /> : <Menu size={26} />}</button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#E9E5DE] px-4 py-4 flex flex-col gap-1">
          {LINKS.map((link, i) => (
            <a key={i} href={link.to} onClick={(e) => go(e, link.to)} className="font-body text-sm text-[#0B1533] py-2.5 border-b border-[#F0EDE7]">{link.label}</a>
          ))}
          <div className="mt-3 flex flex-col gap-2">
            {BRAND.phones.map((p, i) => (
              <a key={i} href={p.href} data-testid={`mobile-phone-${i}`} className="inline-flex items-center justify-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold text-sm rounded-full py-3"><Phone size={16} /> {p.number}{p.label ? ` (${p.label})` : ""}</a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
